import type {
  SDScopeStatus,
  TestCredentialsResult,
} from '@/api/hooks/useTenantConfig'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons'
import { Alert, Modal, Spin, Table, Tag, Typography, theme } from 'antd'
import type { ColumnsType } from 'antd/es/table'

const { Text } = Typography

interface TestCredentialsModalProps {
  open: boolean
  onClose: () => void
  result: TestCredentialsResult | null
  loading: boolean
  instanceName?: string
}

const ERROR_CATEGORY_COLORS: Record<string, string> = {
  connection: 'red',
  authentication: 'orange',
  timeout: 'gold',
  configuration: 'purple',
}

const ERROR_CATEGORY_LABELS: Record<string, string> = {
  connection: 'Connection Error',
  authentication: 'Auth Error',
  timeout: 'Timeout',
  configuration: 'Config Error',
}

export function TestCredentialsModal({
  open,
  onClose,
  result,
  loading,
  instanceName,
}: TestCredentialsModalProps) {
  const { token } = theme.useToken()

  const columns: ColumnsType<SDScopeStatus> = [
    {
      title: 'Status',
      dataIndex: 'present',
      key: 'present',
      width: 80,
      align: 'center',
      render: (present: boolean) =>
        present ? (
          <CheckCircleFilled
            style={{ color: token.colorSuccess, fontSize: 18 }}
          />
        ) : (
          <CloseCircleFilled
            style={{ color: token.colorError, fontSize: 18 }}
          />
        ),
    },
    {
      title: 'Scope',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type='secondary' style={{ fontSize: 12 }}>
            {record.code}
          </Text>
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string) => <Text type='secondary'>{desc}</Text>,
    },
  ]

  const renderContent = () => {
    if (loading) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            gap: 16,
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
          <Text type='secondary'>Testing credentials...</Text>
        </div>
      )
    }

    if (!result) {
      return null
    }

    if (!result.success) {
      return (
        <Alert
          type='error'
          showIcon
          icon={<ExclamationCircleFilled />}
          message={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>Connection Failed</span>
              {result.error_category && (
                <Tag
                  color={
                    ERROR_CATEGORY_COLORS[result.error_category] || 'default'
                  }
                >
                  {ERROR_CATEGORY_LABELS[result.error_category] ||
                    result.error_category}
                </Tag>
              )}
            </div>
          }
          description={result.error_message}
        />
      )
    }

    const missingScopes = result.scopes.filter((s) => !s.present)
    const hasAllScopes = missingScopes.length === 0

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Alert
          type={hasAllScopes ? 'success' : 'warning'}
          showIcon
          message={
            hasAllScopes
              ? 'Authentication successful - all required scopes present'
              : `Authentication successful - ${missingScopes.length} scope${missingScopes.length === 1 ? '' : 's'} missing`
          }
          description={
            !hasAllScopes && (
              <Text type='secondary'>
                Some operations may fail without the missing scopes.
              </Text>
            )
          }
        />
        <Table
          columns={columns}
          dataSource={result.scopes}
          rowKey='code'
          pagination={false}
          size='small'
          bordered
        />
      </div>
    )
  }

  return (
    <Modal
      title={`Test Credentials${instanceName ? ` - ${instanceName}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
    >
      {renderContent()}
    </Modal>
  )
}
